import { DragOverlay } from "@dnd-kit/core"
import { useMemo } from "react"

import { TableBody } from "./TableBody"
import { TableColumns } from "./TableColumns"
import { TableHeader } from "./TableHeader"
import { TableProps } from "./types"
import { classNames } from "../../../../lib/classNames"
import { SortableRowsProvider } from "../../SortableRows/SortableRowsProvider"
import {
  TableEmptyPlaceholder,
  TableErrorPlaceholder,
  TableLoadingPlaceholder,
} from "../TablePlaceholder"

export function Table(props: TableProps) {
  const {
    header,
    body,
    cellHeight,
    isError,
    isLoading,
    onLastRowInView,
    testing,
    rowReordering,
    keyboardNavigation,
    isHeaderSticky = true,
  } = props

  const renderPlaceholder = () => {
    if (isLoading) return <TableLoadingPlaceholder />
    if (isError) return <TableErrorPlaceholder />
    if (body.rows.length === 0) {
      return (
        <TableEmptyPlaceholder
          message={body.emptyPlaceholderMessage ?? "No results found"}
        />
      )
    }
    return null
  }

  const placeholder = renderPlaceholder()

  const tableWidth = useMemo(
    () => header.row.cells.reduce((sum, cell) => sum + cell.width, 0),
    [header.row.cells]
  )

  return (
    <SortableRowsProvider rowReordering={rowReordering}>
      <table
        data-testid={testing?.tableTestId}
        className="min-w-full table-fixed border-collapse"
        style={{ width: tableWidth }}
      >
        <colgroup>
          <TableColumns header={header} />
        </colgroup>
        {/* Opaque, or rows scrolling under show through the collapsed seam */}
        <thead
          className={classNames(
            isHeaderSticky && "bg-input-default sticky top-0 z-10"
          )}
        >
          <TableHeader header={header} cellHeight={cellHeight} />
        </thead>
        {!placeholder && (
          <tbody ref={keyboardNavigation?.listRef}>
            <TableBody
              body={body}
              cellHeight={cellHeight}
              onLastRowInView={onLastRowInView}
              keyboardNavigation={keyboardNavigation}
            />
          </tbody>
        )}
      </table>
      {placeholder}
      <DragOverlay>
        {rowReordering?.draggingRowKey && (
          <table className="min-w-full table-fixed border-collapse opacity-50">
            <tbody>
              <TableBody
                body={{
                  ...body,
                  rows: body.rows.filter(
                    (row) => row.key === rowReordering?.draggingRowKey
                  ),
                }}
                cellHeight={cellHeight}
              />
            </tbody>
          </table>
        )}
      </DragOverlay>
    </SortableRowsProvider>
  )
}
